"use client";

import { forwardRef } from "react";

import Link from "next/link";

/**
 * NextLink - A client component wrapper for Next.js Link
 *
 * This wrapper exists to solve a Next.js 16 breaking change where function components
 * cannot be passed directly to Client Components via props.
 *
 * The pattern `<Button component={Link} href="..." />` (used extensively with Mantine's
 * polymorphic components) throws an error in Next.js 16:
 * "Functions cannot be passed directly to Client Components unless you explicitly
 * expose it by marking it with 'use server'."
 *
 * This wrapper creates an explicit client boundary that can be safely passed to
 * polymorphic components like Mantine's Button, Anchor, ActionIcon, etc.
 *
 * Usage:
 * import { NextLink } from "@/app/lib/components/next/NextLink";
 * <Button component={NextLink} href="/path">Click me</Button>
 *
 * References:
 * - https://github.com/vercel/next.js/issues/42408
 * - https://nextjs-forum.com/post/1430958866757324920
 */
export const NextLink = forwardRef<HTMLAnchorElement, React.ComponentPropsWithoutRef<typeof Link>>(
  (props, ref) => <Link ref={ref} {...props} />,
);

NextLink.displayName = "NextLink";
